import { colors } from '@cabonline/ui-shared';
import { Button, LinkButton, Text } from '@cabonline/ui-web';
import { WarningAmberOutlined, WifiOffOutlined } from '@mui/icons-material';
import { CUSTOMER_SERVICE_EMAIL, useLocale } from '@multiplatform-apps/service-trips';
import React from 'react';

import { isForbiddenError, isNetworkError, isNotFoundError } from '../../utils';
import { ErrorDetailsAccordion } from '../error-details-accordion';
import {
  StyledContactLink,
  StyledErrorContainer,
  StyledErrorWrapper,
} from './styles';
import { ErrorViewProps } from './types';

const ErrorViewBody = ({
  error,
  onReturnHome,
  onReload,
}: Pick<
  ErrorViewProps,
  'error' | 'onReturnHome' | 'onReload'
>): React.ReactElement => {
  const { t } = useLocale();

  if (isForbiddenError(error)) {
    return (
      <>
        <WarningAmberOutlined fontSize="large" aria-hidden="true" />
        <Text as="h1" weight="extraBold" size="xl">
          {t('forbidden.title')}
        </Text>
        <Text weight="semi" size="l" color={colors.gray1}>
          {t('forbidden.description')}
        </Text>
        <Button label={t('error.generic.returnButton')} onClick={onReturnHome} />
      </>
    );
  }

  if (isNetworkError(error)) {
    return (
      <>
        <WifiOffOutlined fontSize="large" aria-hidden="true" />
        <Text as="h1" weight="extraBold" size="xl">
          {t('error.network.title')}
        </Text>
        <Text weight="semi" size="l" color={colors.gray1}>
          {t('error.network.description')}
        </Text>
        <Button label={t('error.generic.tryAgainButton')} onClick={onReload} />
      </>
    );
  }

  if (isNotFoundError(error)) {
    return (
      <>
        <WarningAmberOutlined fontSize="large" aria-hidden="true" />
        <Text as="h1" weight="extraBold" size="xl">
          {t('error.notFound.title')}
        </Text>
        <Text weight="semi" size="l" color={colors.gray1}>
          {t('error.notFound.description')}
        </Text>
        <Button
          label={t('error.notFound.returnButton')}
          onClick={onReturnHome}
        />
      </>
    );
  }

  return (
    <>
      <WarningAmberOutlined fontSize="large" aria-hidden="true" />
      <Text as="h1" weight="extraBold" size="xl">
        {t('error.generic.title')}
      </Text>
      <Text weight="semi" size="l" color={colors.gray1}>
        {t('error.generic.description')}
      </Text>
      <Text size="s" color={colors.gray2}>
        {t('error.generic.contactEmail')}{' '}
        <StyledContactLink href={`mailto:${CUSTOMER_SERVICE_EMAIL}`}>
          {CUSTOMER_SERVICE_EMAIL}
        </StyledContactLink>
      </Text>
      <Button label={t('error.generic.tryAgainButton')} onClick={onReload} />
      <LinkButton
        label={t('error.notFound.returnButton')}
        onClick={onReturnHome}
      />
    </>
  );
};

/**
 * Shared error UI for both the route error page and the app-level ErrorBoundary.
 * Uses no router hooks, so it is safe to render above <RouterProvider>.
 */
export const ErrorView = ({
  error,
  onReturnHome,
  onReload,
  isMobile,
  containerRef,
  forbiddenBody,
}: ErrorViewProps): React.ReactElement => (
  <StyledErrorWrapper isMobile={isMobile}>
    <StyledErrorContainer ref={containerRef} role="status" tabIndex={-1}>
      {isForbiddenError(error) && forbiddenBody ? (
        forbiddenBody
      ) : (
        <ErrorViewBody
          error={error}
          onReturnHome={onReturnHome}
          onReload={onReload}
        />
      )}
      <ErrorDetailsAccordion error={error} />
    </StyledErrorContainer>
  </StyledErrorWrapper>
);
